import { OpenAPI } from '@rbx/docsite-utilities/dist/browser';

/**
 * Helper function to extract component name from reference path
 * @param ref - The reference object
 * @param expectedType - The expected component type
 * @returns The component name or null if invalid
 */
const extractComponentName = (
  ref: OpenAPI.ReferenceObject,
  expectedType: string,
): string | null => {
  if (!ref.$ref || !ref.$ref.startsWith('#/components/')) {
    return null;
  }

  const refPath = ref.$ref.replace('#/components/', '');
  const [type, name] = refPath.split('/');

  if (type !== expectedType) {
    return null;
  }

  return name;
};

type ValueOf<T> = T extends null | undefined ? undefined : T[keyof T];

/**
 * Resolves a component reference.
 * @param document - The OpenAPI document
 * @param ref - The reference string to resolve
 * @param componentType - The type of component to resolve, e.g. schemas
 * @param visited - Set of already visited references to prevent circular resolution
 * @returns The resolved component value
 */
export const resolveReference = <T extends keyof OpenAPI.ComponentsObject>(
  document: OpenAPI.Document,
  ref: OpenAPI.ReferenceObject,
  componentType: T,
  visited: Set<string> = new Set(),
): Exclude<ValueOf<OpenAPI.ComponentsObject[T]>, OpenAPI.ReferenceObject> | undefined => {
  // Check for circular reference
  if (visited.has(ref.$ref)) {
    return undefined;
  }

  const componentName = extractComponentName(ref, componentType);
  if (!componentName || !document.components?.[componentType]) {
    return undefined;
  }

  const component = document.components[componentType][componentName];
  if (!component) {
    return undefined;
  }

  // If it's a reference object, recursively resolve it
  if ('$ref' in component) {
    visited.add(ref.$ref);
    const resolved = resolveReference<T>(
      document,
      component as OpenAPI.ReferenceObject,
      componentType,
      visited,
    );
    visited.delete(ref.$ref);
    return resolved;
  }

  return component as Exclude<ValueOf<OpenAPI.ComponentsObject[T]>, OpenAPI.ReferenceObject>;
};

export default resolveReference;
